import { useCallback, useState } from 'react'

import { AttestationConsentComponent } from '@/components/Chat/AttestationConsent'
import { QuotesEvent, useTrackQuotesEvent } from '@/components/quotes/quotesAnalytics'
import { Button } from '@/components/ui/button'
import { useStaffSession } from '@/hooks/useStaffSession'
import { acceptAttestations, type AttestationConsent, AttestationScope } from '@/models/attestation'
import type { InsuranceApplication } from '@/models/insuranceApplication'
import { isOrgAdmin, useCurrentUser } from '@/models/user'

interface ReviewConfirmationProps {
  application: InsuranceApplication
  /** Refetches the application; resolves once it has landed. */
  onConfirmed: () => Promise<void>
}

/** Cai's review attestation and the button that records it. Only an owner or admin of the org can give it. */
export function ReviewConfirmation({ application, onConfirmed }: ReviewConfirmationProps) {
  const track = useTrackQuotesEvent()
  const staffSession = useStaffSession()
  const { user } = useCurrentUser({ enabled: false })
  const [consents, setConsents] = useState<AttestationConsent[]>([])
  const [allAccepted, setAllAccepted] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [failed, setFailed] = useState(false)

  // Stable identity — AttestationConsentComponent has this in an effect dep list.
  const handleConsentChange = useCallback((accepted: boolean, next: AttestationConsent[]) => {
    setAllAccepted(accepted)
    setConsents(next)
  }, [])

  if (!staffSession && user && !isOrgAdmin(user)) {
    return <p className="text-ink-600 text-base">Ask the business owner to confirm this application.</p>
  }

  const accepted = consents.filter((consent) => consent.bool_value)

  async function confirm() {
    if (accepted.length === 0) return
    setConfirming(true)
    setFailed(false)
    try {
      await acceptAttestations(application.orgId, accepted)
      track(QuotesEvent.REVIEW_ATTESTATION_ACCEPTED, { has_channel_pick: !!application.pendingChannelPick })
      await onConfirmed()
    } catch {
      setFailed(true)
    } finally {
      setConfirming(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <AttestationConsentComponent
        orgId={application.orgId}
        scope={AttestationScope.REVIEW}
        readonly={staffSession}
        onConsentChange={staffSession ? undefined : handleConsentChange}
      />
      {failed && (
        <p role="alert" className="text-destructive text-sm">
          We couldn&apos;t save your confirmation. Please try again.
        </p>
      )}
      <div className="flex justify-end">
        <Button
          size="lg"
          disabled={staffSession || !allAccepted || accepted.length === 0}
          loading={confirming ? 'Confirming…' : undefined}
          onClick={confirm}
        >
          Confirm and see my quotes
        </Button>
      </div>
    </div>
  )
}
